import type {
  ICreateUserMemoryFactPayload,
  IGetUserMemoryOverviewParams,
  IUpdateUserMemoryExclusionsPayload,
  IUpdateUserMemoryFactPayload,
  IUpdateUserMemoryProfilePayload,
  IUserMemoryExclusionsView,
  IUserMemoryFactCreateResult,
  IUserMemoryFactPositionView,
  IUserMemoryFactView,
  IUserMemoryOverview,
  IUserMemoryProfileView,
  IUserMemoryStarterPromptsView,
} from "../types/user-memory";
import type { ApiClient } from "./client";

export interface IUpdateUserMemoryFactParams {
  factUuid: string;
  payload: IUpdateUserMemoryFactPayload;
}

export class UserMemoryApi {
  constructor(private apiClient: ApiClient) {}

  async getOverview({
    limit,
    offset,
    search,
    factType,
  }: IGetUserMemoryOverviewParams = {}): Promise<IUserMemoryOverview> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    if (offset !== undefined) {
      params.set("offset", String(offset));
    }
    if (search) {
      params.set("search", search);
    }
    if (factType) {
      params.set("factType", factType);
    }

    const query = params.toString();
    const path = query ? `/user/memory?${query}` : "/user/memory";

    const response = await this.apiClient.get<IUserMemoryOverview>(path);
    return response.data;
  }

  // NOTE: NSAI-6559 — personalized suggestions for the empty chat, drawn from
  // the memory of the workspace the request's X-Project-Uuid names and written
  // in the X-App-Language interface language. Both headers come from the
  // client, so the caller passes nothing.
  async getStarterPrompts(): Promise<IUserMemoryStarterPromptsView> {
    const response = await this.apiClient.get<IUserMemoryStarterPromptsView>(
      "/user/memory/starter-prompts",
    );
    return response.data;
  }

  // NOTE: NSAI-6751 — where a fact sits in the unfiltered overview list; 404
  // when it is not one of the caller's active facts.
  async getFactPosition(
    factUuid: string,
  ): Promise<IUserMemoryFactPositionView> {
    const response = await this.apiClient.get<IUserMemoryFactPositionView>(
      `/user/memory/facts/${encodeURIComponent(factUuid)}/position`,
    );
    return response.data;
  }

  async updateFact(
    params: IUpdateUserMemoryFactParams,
  ): Promise<IUserMemoryFactView> {
    const response = await this.apiClient.patch<IUserMemoryFactView>(
      `/user/memory/facts/${params.factUuid}`,
      params.payload,
    );
    return response.data;
  }

  async createFact(
    payload: ICreateUserMemoryFactPayload,
  ): Promise<IUserMemoryFactCreateResult> {
    const response = await this.apiClient.post<IUserMemoryFactCreateResult>(
      "/user/memory/facts",
      payload,
    );
    return response.data;
  }

  async updateProfile(
    payload: IUpdateUserMemoryProfilePayload,
  ): Promise<IUserMemoryProfileView> {
    const response = await this.apiClient.patch<IUserMemoryProfileView>(
      "/user/memory/profile",
      payload,
    );
    return response.data;
  }

  async deleteFact(factUuid: string): Promise<void> {
    await this.apiClient.delete(`/user/memory/facts/${factUuid}`);
  }

  async clearAll(): Promise<void> {
    await this.apiClient.delete("/user/memory");
  }

  // NOTE: NSAI-6369 — the read returns the user's own exclusions together with
  // the ones inherited from their organization, so the settings screen can show
  // the whole effective policy in one request without a second call that could
  // render half a list.
  async getExclusions(): Promise<IUserMemoryExclusionsView> {
    const response = await this.apiClient.get<IUserMemoryExclusionsView>(
      "/user/memory/exclusions",
    );
    return response.data;
  }

  // NOTE: A full replacement, not a patch. The editor is a textarea holding the
  // whole list, so "what the user is looking at" is the only sensible payload —
  // and an add / remove API would have to answer what happens when two tabs
  // disagree about the order.
  async updateExclusions(
    payload: IUpdateUserMemoryExclusionsPayload,
  ): Promise<IUserMemoryExclusionsView> {
    const response = await this.apiClient.put<IUserMemoryExclusionsView>(
      "/user/memory/exclusions",
      payload,
    );
    return response.data;
  }
}
